import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Save, Trash2, Lock, ArrowLeft, Loader2, ShieldAlert, UserCog } from "lucide-react";

import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import StatusBadge from "@/components/StatusBadge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const SUPER_ADMIN = "super_admin";

export default function RoleEdit({ mode = "edit" }) {
  const { roleId } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canWrite = hasPermission("settings.roles.write");

  const [role, setRole] = useState(null);
  const [catalogue, setCatalogue] = useState(null); // {grouped, labels, reserved}
  const [allRoles, setAllRoles] = useState([]);
  const [usersCount, setUsersCount] = useState({ total: 0, active: 0 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignTarget, setReassignTarget] = useState("");

  const [form, setForm] = useState({ name: "", display_name: "", description: "" });
  const [perms, setPerms] = useState(new Set());

  const isCreate = mode === "create";
  const isSuperAdmin = role?.name === SUPER_ADMIN;
  const isSystem = role?.is_system;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cat, rolesResp] = await Promise.all([
        api.get("/settings/permissions/list"),
        api.get("/settings/roles?active=true"),
      ]);
      setCatalogue(cat.data);
      setAllRoles(rolesResp.data);
      if (!isCreate) {
        const [roleResp, cntResp] = await Promise.all([
          api.get(`/settings/roles/${roleId}`),
          api.get(`/settings/roles/${roleId}/users-count`),
        ]);
        setRole(roleResp.data);
        setUsersCount(cntResp.data);
        setForm({
          name: roleResp.data.name,
          display_name: roleResp.data.display_name,
          description: roleResp.data.description || "",
        });
        setPerms(new Set(roleResp.data.permissions));
      } else {
        setForm({ name: "", display_name: "", description: "" });
        setPerms(new Set());
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [roleId, isCreate]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (params.get("reassign") === "1" && role && !role.is_system) {
      setReassignOpen(true);
      setParams({});
    }
  }, [params, role, setParams]);

  const nameLocked = isSystem;
  const permsLocked = isSuperAdmin; // super_admin permissions are display-only

  const groupedKeys = useMemo(() => catalogue?.grouped || {}, [catalogue]);
  const reservedSet = useMemo(() => new Set(catalogue?.reserved || []), [catalogue]);

  const togglePerm = (key) => {
    if (permsLocked || !canWrite) return;
    if (reservedSet.has(key)) return; // never toggle a reserved perm on a non-super_admin role
    setPerms((s) => {
      const n = new Set(s);
      if (n.has(key)) n.delete(key); else n.add(key);
      return n;
    });
  };

  const toggleGroup = (group, allOn) => {
    if (permsLocked || !canWrite) return;
    setPerms((s) => {
      const n = new Set(s);
      for (const k of groupedKeys[group] || []) {
        if (reservedSet.has(k)) continue;
        if (allOn) n.delete(k); else n.add(k);
      }
      return n;
    });
  };

  const handleSave = async () => {
    if (!canWrite) return;
    setSaving(true);
    try {
      if (isCreate) {
        const body = {
          name: form.name.trim(),
          display_name: form.display_name.trim() || form.name.trim(),
          description: form.description.trim() || null,
          permissions: Array.from(perms),
        };
        const { data } = await api.post("/settings/roles", body);
        toast.success(`Role "${data.display_name}" created`);
        navigate(`/settings/roles/${data.id}`);
      } else {
        const body = {};
        if (!nameLocked && form.name !== role.name) body.name = form.name.trim();
        if (form.display_name !== role.display_name) body.display_name = form.display_name.trim();
        if ((form.description || "") !== (role.description || "")) body.description = form.description.trim() || null;
        const currentPerms = new Set(role.permissions);
        const nextArr = Array.from(perms);
        const sameSet = currentPerms.size === perms.size && nextArr.every((k) => currentPerms.has(k));
        if (!sameSet && !permsLocked) body.permissions = nextArr;
        if (Object.keys(body).length === 0) {
          toast.info("Nothing to save");
          setSaving(false);
          return;
        }
        const { data } = await api.patch(`/settings/roles/${roleId}`, body);
        toast.success("Role saved");
        setRole(data);
        setForm({ name: data.name, display_name: data.display_name, description: data.description || "" });
        setPerms(new Set(data.permissions));
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (usersCount.active > 0) {
      toast.error(`Reassign ${usersCount.active} active user(s) first`);
      return;
    }
    if (!window.confirm(`Delete role "${role.display_name}"?`)) return;
    try {
      await api.delete(`/settings/roles/${roleId}`);
      toast.success("Role deleted");
      navigate("/settings/roles");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleReassign = async () => {
    if (!reassignTarget) return;
    try {
      const { data } = await api.post(`/settings/roles/${roleId}/reassign-users`, { target_role_name: reassignTarget });
      toast.success(`Moved ${data.moved} user(s) to ${data.to}`);
      setReassignOpen(false);
      setReassignTarget("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (loading || !catalogue || (!isCreate && !role)) {
    return <div className="p-6" data-testid="settings-roles-edit-loading"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  }

  return (
    <div className="p-6 space-y-6 pb-24" data-testid="settings-roles-edit-page">
      <PageHeader
        testId="settings-roles-edit-header"
        title={isCreate ? "New role" : role.display_name}
        subtitle={isCreate ? "Create a custom role and grant permissions." : (
          <span className="flex items-center gap-2">
            {isSystem ? <StatusBadge tone="info"><Lock className="h-3 w-3" /> System role</StatusBadge> : <StatusBadge tone="muted">Custom role</StatusBadge>}
            <span className="font-mono text-xs text-muted-foreground">{role.name}</span>
          </span>
        )}
        actions={
          <Button variant="outline" onClick={() => navigate("/settings/roles")} data-testid="settings-roles-edit-back">
            <ArrowLeft className="h-4 w-4" /> Back
          </Button>
        }
      />

      {isSuperAdmin && (
        <div className="rounded-md border border-info/40 bg-info/10 px-3 py-2 text-xs flex items-start gap-2" data-testid="settings-roles-super-admin-notice">
          <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0 text-info" />
          <div>
            <div className="font-medium">Super Admin has all permissions.</div>
            <div className="text-muted-foreground">
              Permissions on this role are enforced by a hard-coded escape hatch and cannot be edited. Deleting or renaming Super Admin is also blocked.
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="rounded-md lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-sm font-medium">Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Name (internal)" required>
                <Input
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  disabled={nameLocked || !canWrite}
                  placeholder="office_lead"
                  className="font-mono"
                  data-testid="settings-roles-name-input"
                />
                {nameLocked && <div className="text-[10px] text-muted-foreground italic mt-1">System role — name locked.</div>}
              </Field>
              <Field label="Display name" required>
                <Input
                  value={form.display_name}
                  onChange={(e) => setForm((f) => ({ ...f, display_name: e.target.value }))}
                  disabled={!canWrite}
                  placeholder="Office Lead"
                  data-testid="settings-roles-display-name-input"
                />
              </Field>
            </div>
            <Field label="Description">
              <textarea
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                disabled={!canWrite}
                placeholder="Optional — what does this role represent?"
                data-testid="settings-roles-description-input"
                className="w-full min-h-[60px] rounded-sm border border-border bg-background px-3 py-2 text-xs leading-relaxed focus:outline-none focus:border-primary disabled:bg-muted/50"
              />
            </Field>
          </CardContent>
        </Card>

        <Card className="rounded-md">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-sm font-medium">Users on this role</CardTitle>
            {!isCreate && !isSystem && canWrite && (
              <Button variant="outline" size="sm" onClick={() => setReassignOpen(true)} data-testid="settings-roles-reassign-btn">
                <UserCog className="h-3.5 w-3.5" /> Reassign
              </Button>
            )}
          </CardHeader>
          <CardContent className="space-y-2">
            {isCreate ? (
              <div className="text-xs text-muted-foreground italic">Assign users after the role is created.</div>
            ) : (
              <>
                <div className="text-2xl font-bold" data-testid="settings-roles-users-count-active">{usersCount.active}</div>
                <div className="text-xs text-muted-foreground">
                  active user(s){usersCount.total > usersCount.active && ` (${usersCount.total} total incl. inactive)`}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="rounded-md">
        <CardHeader>
          <CardTitle className="text-sm font-medium">Permissions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {Object.entries(groupedKeys).map(([group, keys]) => {
            const nonReserved = keys.filter((k) => !reservedSet.has(k));
            const groupChecked = nonReserved.length > 0 && nonReserved.every((k) => perms.has(k));
            return (
              <div key={group} className="space-y-2" data-testid={`settings-roles-group-${group}`}>
                <div className="flex items-center justify-between">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{group}</div>
                  {!permsLocked && canWrite && (
                    <button
                      type="button"
                      onClick={() => toggleGroup(group, groupChecked)}
                      className="text-[10px] text-primary hover:underline"
                      data-testid={`settings-roles-group-toggle-${group}`}
                    >
                      {groupChecked ? "Clear group" : "Select all"}
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {keys.map((key) => {
                    const isReserved = reservedSet.has(key);
                    const checked = isSuperAdmin ? true : perms.has(key);
                    const disabled = permsLocked || !canWrite || isReserved;
                    return (
                      <label
                        key={key}
                        className={`flex items-center gap-2 rounded-sm border border-border px-2 py-1.5 text-xs ${disabled ? "opacity-60" : "hover:border-primary/40 cursor-pointer"}`}
                        data-testid={`settings-roles-perm-${key}`}
                      >
                        <Checkbox
                          checked={checked}
                          disabled={disabled}
                          onCheckedChange={() => togglePerm(key)}
                          data-testid={`settings-roles-perm-checkbox-${key}`}
                        />
                        <span className="flex-1">
                          <span className="font-mono text-[10px] text-muted-foreground">{key}</span>
                          <span className="ml-2">{catalogue.labels[key]}</span>
                        </span>
                        {isReserved && (
                          <span title="Reserved for Super Admin" data-testid={`settings-roles-perm-reserved-${key}`}>
                            <Lock className="h-3 w-3 text-muted-foreground" />
                          </span>
                        )}
                      </label>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {canWrite && (
        <div className="fixed bottom-0 left-56 right-0 bg-background border-t border-border p-3 flex justify-between items-center z-30" data-testid="settings-roles-actions-bar">
          <div className="flex gap-2">
            {!isCreate && !isSystem && (
              <Button
                variant="outline"
                onClick={handleDelete}
                disabled={usersCount.active > 0}
                title={usersCount.active > 0 ? `Cannot delete — ${usersCount.active} user(s) still assigned` : "Delete role"}
                className="text-destructive"
                data-testid="settings-roles-delete-btn"
              >
                <Trash2 className="h-4 w-4" /> Delete
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate("/settings/roles")} data-testid="settings-roles-cancel-btn">
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="settings-roles-save-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              <span className="ml-1">{isCreate ? "Create role" : "Save changes"}</span>
            </Button>
          </div>
        </div>
      )}

      {/* Reassign users dialog */}
      <Dialog open={reassignOpen} onOpenChange={setReassignOpen}>
        <DialogContent className="max-w-md" data-testid="settings-roles-reassign-dialog">
          <DialogHeader>
            <DialogTitle>Reassign users</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-muted-foreground">
              Move {usersCount.active} user(s) from <span className="font-mono">{role?.name}</span> to a different role.
            </div>
            <Select value={reassignTarget} onValueChange={setReassignTarget}>
              <SelectTrigger data-testid="settings-roles-reassign-target-select">
                <SelectValue placeholder="Select target role" />
              </SelectTrigger>
              <SelectContent>
                {allRoles.filter((r) => r.name !== role?.name).map((r) => (
                  <SelectItem key={r.id} value={r.name} data-testid={`settings-roles-reassign-option-${r.name}`}>
                    {r.display_name} <span className="text-muted-foreground font-mono">({r.name})</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReassignOpen(false)} data-testid="settings-roles-reassign-cancel">
              Cancel
            </Button>
            <Button onClick={handleReassign} disabled={!reassignTarget} data-testid="settings-roles-reassign-confirm">
              Move users
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {label} {required && <span className="text-destructive">*</span>}
      </Label>
      {children}
    </div>
  );
}
